/**
 * Pfeile zum Blaettern in der Bildvergroesserung.
 *
 * Horizon laesst im Zoom-Dialog nur zwei Wege zum naechsten Bild: scrollen oder
 * eine Miniatur anklicken. Auf dem Desktop erwartet man dort Pfeile — und ohne
 * sie ist nicht einmal erkennbar, dass ueberhaupt weitere Bilder da sind.
 *
 * Geblaettert wird NICHT ueber die Scrollposition, sondern ueber Sichtbarkeit:
 * das JS setzt data-buechel-pfeile auf den Dialog, worauf das Snippet
 * buechel-bildvergroesserung.liquid alle Listeneintraege ausser dem aktiven
 * ausblendet. Grund: sobald die Liste im Fenster einmal weitergescrollt ist,
 * laesst sich ihr scrollTop nicht mehr zuruecksetzen — weder ueber
 * scrollIntoView der Theme-Komponente noch ueber scrollTo oder scrollTop
 * direkt; der Wert springt sofort auf das Maximum zurueck. Vorwaerts ging es,
 * rueckwaerts nie. Ohne Scrollen gibt es dieses Problem nicht.
 *
 * Das Attribut ist zugleich die Absicherung: laedt dieses Modul nicht, greifen
 * die Ausblende-Regeln nicht und es bleibt beim bisherigen Scrollen.
 *
 * selectThumbnail() der Theme-Komponente wird trotzdem aufgerufen — es setzt
 * aria-selected auf den Miniaturen und laedt die hohe Aufloesung nach. Nur das
 * Scrollen darin laeuft jetzt ins Leere.
 *
 * Die Schaltflaechen stehen als <template> im Snippet, damit die Symbole aus
 * dem Theme kommen (icon-chevron-left/right.svg).
 *
 * Nur ab 750px Breite — darunter bleibt die Ansicht formatfuellend, dort wischt
 * man.
 */

const BREITE_AB = '(min-width: 750px)';
const AKTIV = 'buechel-zoom-aktiv';

/** @param {Element} zoomDialog */
function pfeileEinbauen(zoomDialog) {
  if (!(zoomDialog instanceof HTMLElement) || zoomDialog.dataset.buechelPfeile) return;

  const dialog = zoomDialog.querySelector('dialog');
  const liste = zoomDialog.querySelector('.dialog-zoomed-gallery');
  const vorlage = document.getElementById('buechel-zoom-pfeile-vorlage');
  if (!dialog || !liste || !(vorlage instanceof HTMLTemplateElement)) return;

  const bilder = Array.from(liste.querySelectorAll(':scope > li'));
  if (bilder.length < 2) return;

  zoomDialog.dataset.buechelPfeile = 'an';
  dialog.append(vorlage.content.cloneNode(true));

  const zurueck = dialog.querySelector('.buechel-zoom-pfeil--zurueck');
  const weiter = dialog.querySelector('.buechel-zoom-pfeil--weiter');
  if (!(zurueck instanceof HTMLButtonElement) || !(weiter instanceof HTMLButtonElement)) return;

  let aktiv = -1;

  const anzeigen = (wunsch) => {
    const ziel = Math.max(0, Math.min(bilder.length - 1, wunsch));
    if (ziel === aktiv) return;
    aktiv = ziel;

    bilder.forEach((bild, i) => bild.classList.toggle(AKTIV, i === ziel));
    zurueck.disabled = ziel <= 0;
    weiter.disabled = ziel >= bilder.length - 1;
  };

  const blaettern = (schritt) => {
    const ziel = aktiv + schritt;
    if (ziel < 0 || ziel >= bilder.length) return;

    anzeigen(ziel);

    const komponente = /** @type {any} */ (zoomDialog);
    if (typeof komponente.selectThumbnail === 'function') {
      komponente.selectThumbnail(ziel, { behavior: 'instant' });
    }
  };

  // Welches Bild hat der Besucher angeklickt? Das Theme markiert die zugehoerige
  // Miniatur, bevor der Dialog aufgeht.
  const indexAusMiniaturen = () => {
    const miniaturen = Array.from(dialog.querySelectorAll('.dialog-thumbnails-list button'));
    const i = miniaturen.findIndex((b) => b.getAttribute('aria-selected') === 'true');
    return i < 0 ? 0 : i;
  };

  zurueck.addEventListener('click', (event) => {
    event.stopPropagation();
    blaettern(-1);
  });

  weiter.addEventListener('click', (event) => {
    event.stopPropagation();
    blaettern(1);
  });

  dialog.addEventListener('keydown', (event) => {
    if (!window.matchMedia(BREITE_AB).matches) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      blaettern(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      blaettern(-1);
    }
  });

  zoomDialog.addEventListener('zoom-media:selected', (event) => {
    const i = /** @type {any} */ (event).detail?.index;
    if (typeof i === 'number') anzeigen(i);
  });

  // Beim Oeffnen scrollt das Theme zum angeklickten Bild. Erst danach uebernimmt
  // das Fenster: das data-Attribut schaltet die Ausblende-Regeln scharf, vorher
  // ist die Liste noch scrollbar und verraet ueber ihre Position, welches Bild
  // gemeint war. Beim Schliessen faellt das Attribut wieder weg, sonst stuende
  // beim naechsten Oeffnen nichts zum Ablesen bereit.
  const uebernehmen = () => {
    const hoehe = liste.clientHeight;
    const ausScroll = hoehe ? Math.round(liste.scrollTop / hoehe) : 0;
    const start = ausScroll > 0 ? ausScroll : indexAusMiniaturen();
    dialog.dataset.buechelPfeile = 'an';
    aktiv = -1;
    anzeigen(start);
  };

  new MutationObserver(() => {
    if (dialog.open) {
      window.setTimeout(uebernehmen, 150);
    } else {
      delete dialog.dataset.buechelPfeile;
      aktiv = -1;
    }
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  // Das Theme laesst das open-Attribut nach dem Schliessen kurz stehen (Animation).
  // Das native close-Ereignis kommt verlaesslich — daran haengt das Aufraeumen.
  dialog.addEventListener('close', () => {
    delete dialog.dataset.buechelPfeile;
    aktiv = -1;
  });

  if (dialog.open) uebernehmen();
}

function allePruefen() {
  for (const zoomDialog of document.querySelectorAll('zoom-dialog')) {
    pfeileEinbauen(zoomDialog);
  }
}

allePruefen();

// Die Galerie wird beim Variantenwechsel neu aufgebaut — dann fehlen die
// Schaltflaechen wieder. Der Beobachter faengt das ab, das data-Attribut
// verhindert doppelte Schaltflaechen.
let geplant = false;
new MutationObserver(() => {
  if (geplant) return;
  geplant = true;
  window.requestAnimationFrame(() => {
    geplant = false;
    allePruefen();
  });
}).observe(document.documentElement, { childList: true, subtree: true });
